import {ReactNode} from 'react'

import {TableColumn} from './types.js'

/**
 * The value at a dotted path in a row ('settings.companyAddress.city'), undefined when a part is missing.
 * 0, false and '' are kept.
 */
export function traverse(obj: unknown, key?: string): unknown {
    if (!key) {
        return undefined
    }

    let current = obj

    for (const part of key.split('.')) {
        if (current === undefined || current === null) {
            return undefined
        }

        current = current[part]
    }

    return current
}

/** What a cell without render shows: column.text, else the value at column.key */
export function getValue<T>(row: T, column: TableColumn<T>): ReactNode {
    if (column.text) {
        return column.text(row)
    }

    const value = traverse(row, column.key)

    return value === undefined || value === null ? '' : value as ReactNode
}

/**
 * The id a column's settings are stored under: id, else key, else title. Undefined when it has none of these (an
 * icon column), see getColumnIds.
 */
export function getColumnId(column: TableColumn): string | undefined {
    return column.id || column.key || column.title || undefined
}

/** The ids of all columns, unique: a column without id or with a taken id gets one from its position */
export function getColumnIds(columns: TableColumn[]): string[] {
    const used = new Set<string>()

    return columns.map((column, index) => {
        let id = getColumnId(column)

        if (!id || used.has(id)) {
            id = `${id || 'column'}-${index}`
        }

        used.add(id)

        return id
    })
}

/** Whether the table can sort on a column: it needs a sort function, text or key (never render) */
export function isSortable(column: TableColumn): boolean {
    return !!(column.sort || column.text || column.key)
}

/**
 * What a column sorts on: column.sort(row, option), else column.text, else the value at column.key. Text goes before
 * key, like the table always did: many keys are ids (customerId, typeId) or not in the row at all (branchName) while
 * the text is what the user reads. Give a column sort to sort a formatted date or amount by its value.
 */
export function getSortValue<T>(row: T, column: TableColumn<T>, option?: string): unknown {
    if (column.sort) {
        return column.sort(row, option)
    }

    if (column.text) {
        return column.text(row)
    }

    return traverse(row, column.key)
}

type Comparable = string | number | undefined

// One collator for every comparison: creating one is slow. numeric sorts 'Order 9' before 'Order 10', base ignores
// case and accents.
const collator = new Intl.Collator('nl', {numeric: true, sensitivity: 'base'})

/**
 * Turns a sort value into a number or string, or undefined when it is empty (undefined, null, '', NaN, an invalid
 * date) or not sortable (a React element).
 */
export function toComparable(value: unknown): Comparable {
    if (typeof value === 'number') {
        return Number.isNaN(value) ? undefined : value
    }

    if (typeof value === 'string') {
        return value.trim() === '' ? undefined : value
    }

    if (typeof value === 'boolean') {
        return value ? 1 : 0
    }

    if (value instanceof Date) {
        const time = value.getTime()

        return Number.isNaN(time) ? undefined : time
    }

    return undefined
}

/** Ascending order of two comparable values, empty values first */
export function compareComparables(a: Comparable, b: Comparable): number {
    if (a === undefined || b === undefined) {
        return a === b ? 0 : a === undefined ? -1 : 1
    }

    if (typeof a === 'number' && typeof b === 'number') {
        return a - b
    }

    return collator.compare(String(a), String(b))
}

/** Ascending order of two sort values: numbers, dates and booleans by value, strings naturally, empty values first */
export function compareValues(a: unknown, b: unknown): number {
    return compareComparables(toComparable(a), toComparable(b))
}

/**
 * The rows sorted on a column. Each sort value is computed once (not on every comparison). Empty values go first
 * ascending and last descending, like '' sorts before any text, so rows without a value (like unconfirmed parcels
 * without a date) can be brought to the top. Rows with equal values keep their order.
 */
export function sortRows<T>(rows: T[], column: TableColumn<T> | undefined, descending: boolean, option?: string): T[] {
    if (!column || !isSortable(column)) {
        return rows
    }

    const entries = rows.map((row, index) => ({row, index, value: toComparable(getSortValue(row, column, option))}))

    entries.sort((a, b) => {
        const order = compareComparables(a.value, b.value)

        return (descending ? -order : order) || a.index - b.index
    })

    return entries.map((entry) => entry.row)
}
